/*
 * keep-in-touch.js — dual-write submit handler for the SMS→Email migration page.
 *
 * On submit:
 *   1. Subscribe the email to the store's "SMS→Email Migration" Klaviyo list
 *      (this is the trigger for the discount-code flow — it MUST succeed).
 *   2. Create/update the Shopify customer with the source tag (record of truth;
 *      best-effort — if it fails, we still surface success because the code is
 *      already on its way).
 *
 * Config is read from data-* attributes on the form wrapper so this file is
 * identical across all three stores.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-kit-form]');
  if (!root) return;

  var form = root.querySelector('form');
  var emailInput = root.querySelector('[data-kit-email]');
  var emailError = root.querySelector('[data-kit-email-error]');
  var emailOk = root.querySelector('[data-kit-email-ok]');
  var consentInput = root.querySelector('[data-kit-consent]');
  var submitBtn = root.querySelector('[data-kit-submit]');
  var consentError = root.querySelector('[data-kit-consent-error]');
  var status = root.querySelector('[data-kit-status]');
  var success = document.querySelector('[data-kit-success]');
  var confettiContainer = document.querySelector('[data-kit-confetti]');


  var config = {
    apiEndpoint: root.dataset.apiEndpoint || '',
    apiKey: root.dataset.apiKey || '',
    domain: root.dataset.domain || window.location.hostname
  };

  var honeypot = root.querySelector('[data-kit-honeypot]');
  var formLoadedAt = Date.now();
  var MIN_SUBMIT_TIME = 1500; // ms — minimum human interaction time
  var isSubmitting = false;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function isValidEmail(value) {
    return EMAIL_RE.test(String(value || '').trim());
  }

  function updateSubmitState() {
    // Button is always visually enabled; validation happens on click
  }

  function showEmailError(message) {
    if (!message) {
      emailError.hidden = true;
      emailError.textContent = '';
      emailInput.removeAttribute('aria-invalid');
    } else {
      emailError.hidden = false;
      emailError.textContent = message;
      emailInput.setAttribute('aria-invalid', 'true');
    }
  }

  function showEmailValid(ok) {
    if (emailOk) emailOk.hidden = !ok;
  }

  function showConsentError(show) {
    if (consentError) {
      consentError.classList.toggle('is-visible', !!show);
    }
  }

  function showStatus(message, kind) {
    if (!message) {
      status.hidden = true;
      status.textContent = '';
      delete status.dataset.kind;
    } else {
      status.hidden = false;
      status.textContent = message;
      status.dataset.kind = kind || '';
    }
  }

  var emailDebounce;
  emailInput.addEventListener('input', function () {
    clearTimeout(emailDebounce);
    emailDebounce = setTimeout(function () {
      var value = emailInput.value;
      if (value && !isValidEmail(value)) {
        showEmailError("That doesn't look like a valid email address.");
        showEmailValid(false);
      } else {
        showEmailError('');
        showEmailValid(isValidEmail(value));
      }
      updateSubmitState();
    }, 120);
  });

  emailInput.addEventListener('blur', function () {
    if (emailInput.value && !isValidEmail(emailInput.value)) {
      showEmailError("That doesn't look like a valid email address.");
    }
  });

  consentInput.addEventListener('change', updateSubmitState);

  function submitToSignupApi(email) {
    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, 10000);

    if (!config.apiEndpoint || !config.apiKey) {
      return Promise.reject(new Error('Signup API endpoint or key not configured.'));
    }
    return fetch(config.apiEndpoint, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey
      },
      body: JSON.stringify({
        email: email,
        domain: config.domain,
        customSource: 'Keep in Touch'
      })
    }).then(function (res) {
      clearTimeout(timeout);
      return res.json().then(function (data) {
        if (!res.ok) {
          throw new Error(data.message || 'Signup API failed (' + res.status + ')');
        }
        if (!data.success) {
          throw new Error(data.message || 'Signup API returned success: false');
        }
        return data;
      });
    }).catch(function (err) {
      clearTimeout(timeout);
      if (err.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw err;
    });
  }

  // Build the Shopify request body. Must be called BEFORE lock() disables the
  // fields — disabled controls are excluded from FormData, which would silently
  // drop contact[email] and make Shopify reject the customer create.
  function serializeForm(formEl) {
    return new URLSearchParams(new FormData(formEl));
  }

  function postShopifyCustomer(body) {
    return fetch('/contact#contact_form', {
      method: 'POST',
      headers: { 'Accept': 'text/html' },
      body: body,
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('Shopify customer create failed (' + res.status + ')');
    });
  }

  function renderSuccess() {
    // Hide the whole form wrapper, not just <form>: .kit__form sets display:grid,
    // which would override the [hidden] attribute and leave the form visible.
    root.hidden = true;
    if (success) {
      success.hidden = false;
      try { success.focus({ preventScroll: false }); } catch (e) { /* noop */ }
    }
    showStatus('', '');
    launchConfetti();
  }

  function lock() {
    isSubmitting = true;
    submitBtn.blur();
    emailInput.disabled = true;
    consentInput.disabled = true;
    submitBtn.setAttribute('aria-busy', 'true');
    showStatus('Sending — hold tight…', 'loading');
  }

  function unlock() {
    isSubmitting = false;
    emailInput.disabled = false;
    consentInput.disabled = false;
    submitBtn.removeAttribute('aria-busy');
  }

  function launchConfetti() {
    if (!confettiContainer) return;
    var colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE'];
    var count = 40;
    for (var i = 0; i < count; i++) {
      var piece = document.createElement('div');
      piece.className = 'kit__confetti-piece';
      piece.style.left = Math.random() * 100 + '%';
      piece.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      piece.style.width = (4 + Math.random() * 8) + 'px';
      piece.style.height = (4 + Math.random() * 8) + 'px';
      piece.style.borderRadius = Math.random() > 0.5 ? '50%' : '0';
      piece.style.animationDelay = (Math.random() * 0.8) + 's';
      piece.style.animationDuration = (1.5 + Math.random() * 1.5) + 's';
      confettiContainer.appendChild(piece);
    }
    setTimeout(function () {
      confettiContainer.innerHTML = '';
    }, 3500);
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    showConsentError(false);
    showEmailError('');
    showStatus('', '');

    var email = emailInput.value.trim();
    if (!isValidEmail(email)) {
      showEmailError("That doesn't look like a valid email address.");
      emailInput.focus();
      return;
    }
    if (!consentInput.checked) {
      showConsentError(true);
      consentInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Bot detection: honeypot must be empty and form must not be submitted too fast
    if (honeypot && honeypot.value.trim() !== '') {
      // Silently drop bot submissions — don't tip them off
      return;
    }
    if (Date.now() - formLoadedAt < MIN_SUBMIT_TIME) {
      // Too fast for a human — silently drop
      return;
    }
    if (isSubmitting) return;
    // Snapshot the Shopify payload while the fields are still enabled.
    var shopifyBody = serializeForm(form);
    lock();
    submitToSignupApi(email)
      .then(function () {
        return postShopifyCustomer(shopifyBody).catch(function (err) {
          // Shopify failure is non-blocking — the code is already triggered.
          if (window.console && console.warn) {
            console.warn('[keep-in-touch] Shopify customer create failed:', err);
          }
        });
      })
      .then(renderSuccess)
      .catch(function (err) {
        if (root.hidden) return;
        if (window.console && console.error) {
          console.error('[keep-in-touch] submit failed:', err);
        }
        showStatus('Something went wrong. Please try again.', 'error');
        unlock();
      });
  });

  // Button is always enabled; validation is handled on submit click

  // --- Text fitting for hero headline ---
  function fitText(el, maxPx, minPx) {
    if (!el) return;
    var container = el.closest('.kit__inner') || el.parentElement;
    if (!container) return;

    var maxWidth = container.clientWidth;
    if (maxWidth === 0) return;
    var buffer = 8;

    var originalWhiteSpace = el.style.whiteSpace;
    var originalDisplay = el.style.display;
    el.style.whiteSpace = 'nowrap';
    el.style.display = 'inline-block';

    var minSize = (minPx || 34);
    var maxSize = (maxPx || 140);
    var bestSize = minSize;

    for (var i = 0; i < 15; i++) {
      var mid = (minSize + maxSize) / 2;
      el.style.fontSize = mid + 'px';
      if (el.scrollWidth <= maxWidth - buffer) {
        bestSize = mid;
        minSize = mid;
      } else {
        maxSize = mid;
      }
    }

    el.style.fontSize = bestSize + 'px';
    el.style.whiteSpace = originalWhiteSpace;
    el.style.display = originalDisplay;
  }

  var heroValue = document.querySelector('.kit__hero-value');
  var heroCaption = document.querySelector('.kit__hero-caption');

  function runFit() {
    requestAnimationFrame(function () {
      fitText(heroValue, 140, 34);
      fitText(heroCaption, 60, 24);
    });
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(runFit);
  } else {
    runFit();
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(runFit, 150);
  });
})();
